import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './import-protocol-files-view.html';
import type { ImportProtocol, ImportProtocolEntry } from '../services/api.types';
import { getImportProtocol } from '../services/api.service';
import { ConfirmDialog } from '../components/confirm-dialog';

@customElement({ name: 'import-protocol-files-view', template })
export class ImportProtocolFilesView {
  private readonly i18n: I18N = resolve(I18N);

  public protocol: ImportProtocol | null = null;
  public isFetching = false;
  public fetchError = '';

  public async loading(): Promise<void> {
    const numberText = new URLSearchParams(window.location.search).get('number');
    const number = Number(numberText);
    if (!Number.isSafeInteger(number) || number <= 0) {
      this.fetchError = this.i18n.tr('importProtocols.invalidNumber');
      return;
    }

    this.isFetching = true;
    try {
      this.protocol = await getImportProtocol(number);
    } catch (error: unknown) {
      this.fetchError = error instanceof Error
        ? error.message
        : this.i18n.tr('importProtocols.loadFailed');
    } finally {
      this.isFetching = false;
    }
  }

  public goBack(): void {
    window.location.href = '/import-protocols';
  }

  public openDocument(documentId?: string | null): void {
    if (!documentId) return;
    window.location.href = `/documents?openDocumentId=${encodeURIComponent(documentId)}`;
  }

  public outcomeLabel(outcome: string): string {
    const translated = this.i18n.tr(`importProtocols.outcomes.${outcome}`);
    return translated === `importProtocols.outcomes.${outcome}` ? outcome : translated;
  }

  public formatDate(value?: string | null): string {
    return value ? new Date(value).toLocaleString() : '';
  }

  public documentLabel(entry: ImportProtocolEntry, kind: 'source' | 'current' | 'target'): string {
    const title = kind === 'source'
      ? entry.sourceDocumentTitle
      : kind === 'current'
        ? entry.currentDocumentTitle
        : entry.targetDocumentTitle;
    return title || '—';
  }

  public documentId(entry: ImportProtocolEntry, kind: 'source' | 'current' | 'target'): string | null {
    return kind === 'source'
      ? entry.sourceDocumentId ?? null
      : kind === 'current'
        ? entry.currentDocumentId ?? null
        : entry.targetDocumentId ?? null;
  }

  public async showError(error?: string | null): Promise<void> {
    if (!error) return;
    await ConfirmDialog.alert(error, this.i18n.tr('importProtocols.errorDetails'));
  }
}
